'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { MarkdownContent } from '@boty/ui';
import type { HelpChatMessage } from './types';

/**
 * AI-powered help chat for answering questions about Boty.
 *
 * Uses the Boty AI system with pre-loaded knowledge about the product
 * from the product brief. Provides real, contextual answers about
 * Boty's features, pricing, use cases, and how it works.
 *
 * Epic 6 Note: This component demonstrates "Boty answering questions
 * about Boty" - the same approach will be used on the marketing site.
 */
export function HelpChat() {
  const t = useTranslations('creator.landing.helpChat');
  const locale = useLocale();

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<HelpChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll within the chat container (NOT the page) when messages change.
  // scrollIntoView would scroll the whole page on mobile, hiding the chat.
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      const el = messagesContainerRef.current;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;

      const question = input.trim();
      const userMessage: HelpChatMessage = {
        id: `user-${Date.now()}`,
        text: question,
        isUser: true,
      };

      // Add user message and create placeholder for bot response
      const botMessageId = `bot-${Date.now()}`;
      const botMessage: HelpChatMessage = {
        id: botMessageId,
        text: '',
        isUser: false,
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, botMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const response = await fetch('/api/help-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: question, locale }),
        });

        if (!response.ok) {
          throw new Error('Failed to get response');
        }

        // Handle SSE streaming
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6);
            if (data === '[DONE]') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId ? { ...msg, isStreaming: false } : msg
                )
              );
              break;
            }

            try {
              const parsed = JSON.parse(data);
              if ('content' in parsed) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId
                      ? { ...msg, text: msg.text + parsed.content }
                      : msg
                  )
                );
              }
            } catch {
              // Ignore parse errors for individual chunks
            }
          }
        }
      } catch {
        // On error, show fallback message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  text: locale === 'he'
                    ? 'מצטער, לא הצלחתי לענות. נסו שוב מאוחר יותר.'
                    : 'Sorry, I couldn\'t answer that. Please try again later.',
                  isStreaming: false,
                }
              : msg
          )
        );
      } finally {
        setIsLoading(false);
      }
    },
    [input, locale, isLoading]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          pb: 1,
        }}
      >
        <HelpOutlineIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600}>
          {t('title')}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, pb: 2 }}>
        {/* Messages */}
        {messages.length > 0 && (
          <Box
            ref={messagesContainerRef}
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  alignSelf: msg.isUser ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.isUser ? 'primary.main' : 'action.hover',
                  color: msg.isUser ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {msg.isUser ? (
                  <Typography variant="body2">{msg.text}</Typography>
                ) : msg.isStreaming && !msg.text ? (
                  <CircularProgress size={12} />
                ) : (
                  <MarkdownContent sx={{ fontSize: '0.875rem' }}>
                    {msg.text}
                  </MarkdownContent>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Input form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', gap: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('placeholder')}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={!input.trim() || isLoading}
            aria-label={t('askButton')}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}
